// pages/sections/groups/clientGroupingDemoData.js
//
// Tour-only sample data for the Client Relationship Groups DQ module. Shown
// ONLY while the spotlight tour runs (gated by lib/tourDemo.js) so every step
// always has an ideal example to point at, regardless of the viewer's real
// book. Fictional clients are plausible-as-real grouping puzzles (a nerd
// notices the franchises) — and matches the dgGroupsDQ response shape consumed
// by ClientGroupsSection → DataQualityPanel.
//
// Archivable: delete this file + lib/tourDemo.js and revert the call-site edits.

const VIEWER = 'demo.partner@bmss.com';   // "your" clients in the tour
const OFFICE = 'FHP';
const NOW = '2026-06-30T12:00:00.000Z';

// Minimal PE member — most fields null unless a step renders them.
const mem = (code, name, entityType, extra = {}) => ({
  code, inActivePeRoster: true, name, entityType,
  partner: 'Demo Partner', partnerEmail: VIEWER, manager: 'Demo Manager',
  office: OFFICE, taxpayerEmail: null, spouseEmail: null,
  address: null, city: null, state: null, postCode: null, phone: null,
  peParentCode: null, ...extra,
});

let _id = 7000;
const iss = (issueCode, severity, primary, affected, opts = {}) => ({
  id: _id++, issueCode, severity,
  primaryClientCode: primary.code, primaryClientName: primary.name,
  partnerEmail: VIEWER, office: OFFICE,
  anchorPartnerEmail: opts.anchorPartnerEmail ?? null,
  viewerIsAnchorPartner: opts.viewerIsAnchorPartner ?? true,
  affectedCodes: affected.map((m) => m.code), members: affected,
  rgClientCount: opts.rgClientCount ?? null, rgSgCount: opts.rgSgCount ?? null,
  confirmed: opts.confirmed,            // only set (false) for RG_NEEDS_CONFIRMATION
  details: opts.details ?? null, createdAt: NOW, client: affected[0] ?? null,
});

// ── Cast (fictional, themed) ──
const dm     = mem('DEMO-DM01', 'Dunder Mifflin Paper Co.',  'BUSINESS');
const mscott = mem('DEMO-MS01', 'Michael G. Scott',          'INDIVIDUAL');
const rye    = mem('DEMO-RA01', 'Rye & Sons Aviation',       'BUSINESS');
const nick   = mem('DEMO-NR01', 'Nicholas Rye',              'INDIVIDUAL');
const kim    = mem('DEMO-KR01', 'Kim Rye',                   'INDIVIDUAL');
const leslie = mem('DEMO-LK01', 'Leslie B. Knope',           'INDIVIDUAL');
const ben    = mem('DEMO-BW01', 'Benjamin Wyatt',            'INDIVIDUAL');
const addr   = { address: '4317 Sullivan St', city: 'Pawnee', state: 'IN', postCode: '46001' };
const ron    = mem('DEMO-RS01', 'Ronald U. Swanson',         'INDIVIDUAL', addr);
const duke   = mem('DEMO-DS01', 'Duke Silver',               'INDIVIDUAL', addr);
const lph    = mem('DEMO-LP01', 'Los Pollos Hermanos, LLC',  'BUSINESS');
const ingen  = mem('DEMO-IG01', 'InGen Technologies, Inc.',  'BUSINESS');
const hammond = mem('DEMO-JH01', 'John P. Hammond',          'INDIVIDUAL');
const jpark  = mem('DEMO-JP01', 'Jurassic Park, Inc.',       'BUSINESS');
const thorin = mem('DEMO-TO01', 'Thorin Oakenshield',        'INDIVIDUAL');
const scrooge = mem('DEMO-SM01', 'Scrooge McDuck',           'INDIVIDUAL');

const ISSUES = [
  // Step 1 — Resolve invalid data (ERROR). SG_SOURCE_DISAGREEMENT is pinned
  // first in the panel, so it's the group the tour pre-opens for the Resolve step.
  iss('SG_SOURCE_DISAGREEMENT', 'ERROR', dm, [dm, mscott], {
    details: {
      knuula_sg: 'DEMO-MS01', pe_sg: 'DEMO-DM01', suggested_sg: 'DEMO-MS01',
      knuula_members: [
        { clientCode: dm.code, clientName: dm.name },
        { clientCode: mscott.code, clientName: mscott.name },
      ],
      pe_members: [{ clientCode: dm.code, clientName: dm.name }],
    },
  }),
  iss('SG_BUSINESS_INDIVIDUAL_MIXED', 'ERROR', rye, [rye, nick, kim], {
    details: {
      sg_group_id: 'DEMO-RA01',
      members_summary: '1 business (1120) + 2 individuals (1040)',
      rule_note: 'A signer group should not mix a business entity with unrelated individuals.',
    },
  }),

  // Step 2 — Confirm signer groups (WARN / INFO).
  iss('SG_NEEDS_CONFIRMATION', 'WARN', leslie, [leslie, ben], {
    details: { suggested_sg: 'DEMO-LK01', knuula_sg: 'DEMO-LK01', pe_sg: 'DEMO-LK01' },
  }),
  iss('SG_SUGGESTED_BY_ADDRESS', 'INFO', ron, [ron, duke], {
    details: { matchValue: '4317 Sullivan St, Pawnee, IN 46001' },
  }),

  // Step 4 — Confirm relationship groups (WARN). (Step 3 needs no issue —
  // signoffs.sg = null below makes the SG sign-off bar actionable once unlocked.)
  iss('ORPHAN_CLIENT_RG', 'WARN', lph, [lph]),
  iss('RG_NEEDS_CONFIRMATION', 'WARN', ingen, [ingen, hammond, jpark], {
    confirmed: false, anchorPartnerEmail: VIEWER, rgClientCount: 6, rgSgCount: 3,
  }),

  // Step 5 — Review standalone individuals (WARN).
  iss('STANDALONE_INDIVIDUAL_NEEDS_REVIEW', 'WARN', thorin, [thorin]),
  iss('STANDALONE_INDIVIDUAL_NEEDS_REVIEW', 'WARN', scrooge, [scrooge]),
];

// Roll issues up by (issueCode, severity) → BreakdownRow[].
const roll = (list) => Object.values(list.reduce((acc, i) => {
  const k = `${i.issueCode}|${i.severity}`;
  (acc[k] = acc[k] || { issueCode: i.issueCode, severity: i.severity, count: 0 }).count++;
  return acc;
}, {}));

export const CLIENT_GROUPING_DEMO = {
  period: 'DEMO', scope: 'mine', partnerEmail: VIEWER, isAdmin: false, isSteering: false,
  breakdown: roll(ISSUES),
  openBreakdown: roll(ISSUES),   // nothing confirmed yet → same as breakdown
  totalOpen: ISSUES.length,
  issues: ISSUES,
  rgMemberships: [
    {
      anchorCode: 'DEMO-AP01', anchorName: 'Aperture Science, Inc.',
      anchorPartner: 'cave.johnson@bmss.com', sgCount: 2, clientCount: 4, confirmed: true,
    },
  ],
  signoffs: { sg: null, rg: null, standalone: null },
  truncated: false,
};
